import { useMutation, useQueryClient } from "@tanstack/vue-query";

import { useFetchSiteInstalledApplianceCategories } from "@/composables/kili/sites/fetches/use-fetch-site-installed-appliance-categories";
import { assertProgramBelongsToSite } from "@/composables/kili/sites/queries/program-query-identity";
import { useServices } from "@/configuration/plugins/app/app-provider.configuration";
import { InternalMutationKeys } from "@/data/api/kili/internal/internal-query-keys";
import { SitesQueryKeys } from "@/data/api/kili/sites/sites-query-keys";
import { mapSubprogramsDto } from "@/data/api/v4b/program/subprogram.mapping";
import { V4HSiteId, ProgramId } from "@/data/common/ids";
import {
  defaultProgramColorName,
  EProgramApplianceCategory,
  ProgramDetails,
} from "@/domains/v4b/program/program.types";
import { getProgramApplianceCategories } from "@/utils/programs/program-utils";

export type TDuplicateProgramCommand = {
  name: string;
  siteId: V4HSiteId;
  copiedSiteProgram: {
    siteId: number;
    programId: ProgramId;
    name: string;
  };
};

export function useDuplicateProgramMutation(defaultAppliances: readonly EProgramApplianceCategory[]) {
  const v4bClientSdk = useServices().v4b.clientSdk;
  const queryClient = useQueryClient();
  const { fetch: fetchInstalledApplianceCategories } = useFetchSiteInstalledApplianceCategories();

  return useMutation({
    mutationKey: InternalMutationKeys.createSiteProgram(),
    mutationFn: async (body: TDuplicateProgramCommand) => {
      let maybeCopiedSiteProgram: ProgramDetails | undefined = await queryClient.getQueryData(
        SitesQueryKeys.programDetails(body.copiedSiteProgram.siteId, body.copiedSiteProgram.programId),
      );

      const maybeInstalledApplianceCategories = await fetchInstalledApplianceCategories(body.copiedSiteProgram.siteId);

      const uniqueInstalledApplianceCategories = getProgramApplianceCategories(
        defaultAppliances,
        maybeInstalledApplianceCategories,
      );

      if (!maybeCopiedSiteProgram) {
        maybeCopiedSiteProgram = assertProgramBelongsToSite(
          await v4bClientSdk.siteProgram.getProgramDetails(
            body.copiedSiteProgram.programId,
            uniqueInstalledApplianceCategories ?? [],
          ),
          body.copiedSiteProgram.siteId,
        );

        queryClient.setQueryData(
          SitesQueryKeys.programDetails(body.copiedSiteProgram.siteId, body.copiedSiteProgram.programId),
          maybeCopiedSiteProgram,
        );
      } else {
        assertProgramBelongsToSite(maybeCopiedSiteProgram, body.copiedSiteProgram.siteId);
      }

      const result = await v4bClientSdk.siteProgram.createProgram({
        name: body.name,
        siteId: body.siteId,
        frontDisplayColor: maybeCopiedSiteProgram.frontDisplayColor ?? defaultProgramColorName,
        subprograms: mapSubprogramsDto(maybeCopiedSiteProgram.subprograms),
      });

      if (body.siteId !== body.copiedSiteProgram.siteId) {
        await queryClient.removeQueries({
          queryKey: SitesQueryKeys.programs(body.siteId),
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: SitesQueryKeys.programs(body.siteId),
        });
      }

      return result;
    },
  });
}
